import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/lib/supabase';
import { useSession } from '@/lib/auth';
import { useActiveLocationId } from '@/lib/locationContext';
import { useFeature } from '@/lib/useFeature';
import { viewAsProId, viewRpcArgs } from '@/lib/viewAs';
import { absolute } from '@/lib/hosts';
import { isCell } from '@/lib/cell/config';
import { cellGet, viewQuery } from '@/lib/cell/api';

/**
 * YOUR DRAWING (PRO-DRAW-1; Peter, 2026-10-14, decision 8d40b94e, open item da70a869). The Grand Opening door
 * signs carry a QR to each pro's drawing, and the pro could not see who signed up. "I need everything from day
 * one … so that on the day of, when we open it up, they'll be able to see who was signed up and keep track of
 * it themselves."
 *
 * The reads are two rpcs in image-studios-backend supabase/migrations/20261024120000_pro_hub_drawings.sql:
 *   pro_my_drawings(p_location, p_view_pro)     — the pro's own live/archived drawings (giveaways.host_pro_id)
 *   pro_drawing_entrants(p_giveaway, p_view_pro) — first, last, email of the entrants told the sponsor gets them
 * They decide everything (own drawing only, the 'drawings' switch, never a phone); this file only words it.
 *
 * THE RULE IN WORDS is the owner's sponsor report's (image-os-web src/lib/sponsor-report.ts reportSummary, copied
 * below with the seam marked): everyone who entered is COUNTED; only those who were told up front that their
 * name and email go to this business are LISTED.
 *
 * PRO-DRAW-5 (Peter, 2026-10-18, decisions 169a061c + b1d052cb): the morning after the Grand Opening the winners
 * were not on the pro's page — a multi-prize drawing is a PARENT (the door QR, holding the sign-ups) plus one child
 * drawing per prize, and the wins sat on the children. pro_my_drawings (redefined in image-studios-backend
 * supabase/migrations/20261026090000_pro_draw_5_prizes_and_winners.sql) now answers ONE row per parent with a
 * `prizes` column: every prize (child drawing, round, or the drawing itself) with its winner IN FULL — first name,
 * last name, email, phone — and the draw's record (entry number, drawn at, drawn by, hat size, any redraw with its
 * reason), "so if there is a dispute as to who won, we have this record". The winner is never a crowd name here:
 * the pro hands over the prize.
 */

export interface Round { round: number; prize: string | null; draw_at: string | null }

/** The standing winner of one prize, in full, with the draw's stamp (PRO-DRAW-5). */
export interface Winner {
  first_name: string;
  last_name: string;
  email: string | null;
  phone: string | null;
  entry_number: number | null;
  drawn_at: string;
  drawn_by: string | null;
  pool_size: number | null;
}

/** A draw for this prize that was set aside ("Draw again" — not present, …), with its reason. */
export interface Redraw {
  first_name: string;
  last_name: string;
  entry_number: number | null;
  drawn_at: string;
  superseded_at: string;
  reason: string | null;
}

/** One prize of a drawing: a child drawing, a round, or the drawing itself. */
export interface Prize {
  giveaway_id: string;
  no: number;
  prize: string;
  prize_image: string | null;
  winner: Winner | null;
  redraws?: Redraw[];
}

export interface MyDrawing {
  id: string;
  name: string;
  prize: string;
  prize_image: string | null;
  slug: string;
  status: 'live' | 'archived';
  mode: 'single' | 'scheduled';
  opens_at: string | null;
  closes_at: string | null;
  draw_at: string | null;
  sponsor: string | null;
  entries: number;
  listed: number;
  /** Crowd names ("Maya R."), kept for older readers; the page shows `prizes` instead. */
  winners: string[];
  rounds: Round[] | null;
  /** PRO-DRAW-5: every prize with its winner in full. Null only from a backend older than the migration. */
  prizes: Prize[] | null;
}

/** phone is null unless the entrant's page said the sponsor receives it (PRO-DRAW-2, decision a13f1766). */
export interface Entrant { first_name: string; last_name: string; email: string; phone: string | null; marketing_opt_in?: boolean; entered_at: string }

/** How often the page and the Home block re-read, so a sign-up at the door shows up within a minute. */
export const REFRESH_MS = 30_000;

/** The public sign-up page the door QR opens (image-os-web routes /win/:slug, served on the Owner Hub host). */
export const entryUrl = (slug: string): string => absolute(`/win/${slug}`);

/** The pro's drawings at the active studio — [] when the switch is off or they host none. */
export function useMyDrawings() {
  const { session } = useSession();
  const userId = session?.user?.id;
  const { activeLocationId } = useActiveLocationId();
  const feature = useFeature('drawings');
  const viewing = viewAsProId();
  const q = useQuery<MyDrawing[]>({
    queryKey: ['pro-drawings', userId, activeLocationId, viewing],
    enabled: !!userId && !!activeLocationId && feature.enabled,
    refetchInterval: REFRESH_MS,
    queryFn: async () => {
      if (isCell()) {
        // CELL (M28): GET /v1/pro-hub/locations/{locationId}/drawings — the same rpc (pro_my_drawings).
        const { data, error } = await cellGet<{ rows: MyDrawing[] }>(`/v1/pro-hub/locations/${encodeURIComponent(activeLocationId!)}/drawings`, viewQuery());
        if (error) throw new Error(error.message);
        return (data?.rows ?? []) as MyDrawing[];
      }
      const { data, error } = await supabase.rpc('pro_my_drawings', { p_location: activeLocationId!, ...viewRpcArgs() });
      if (error) throw new Error(error.message);
      return (data ?? []) as MyDrawing[];
    },
  });
  return { ...q, featureLoading: feature.loading, featureOn: feature.enabled };
}

/** Who signed up for one drawing, newest first. */
export function useEntrants(giveawayId: string | null) {
  const viewing = viewAsProId();
  return useQuery<Entrant[]>({
    queryKey: ['pro-drawing-entrants', giveawayId, viewing],
    enabled: !!giveawayId,
    refetchInterval: REFRESH_MS,
    queryFn: async () => {
      if (isCell()) {
        // CELL (M28): GET /v1/pro-hub/giveaways/{giveawayId}/entrants — the same rpc (pro_drawing_entrants).
        const { data, error } = await cellGet<{ rows: Entrant[] }>(`/v1/pro-hub/giveaways/${encodeURIComponent(giveawayId!)}/entrants`, viewQuery());
        if (error) throw new Error(error.message);
        return (data?.rows ?? []) as Entrant[];
      }
      const { data, error } = await supabase.rpc('pro_drawing_entrants', { p_giveaway: giveawayId!, ...viewRpcArgs() });
      if (error) throw new Error(error.message);
      return (data ?? []) as Entrant[];
    },
  });
}

export const people = (n: number): string => `${n} ${n === 1 ? 'person' : 'people'}`;

/** "Christy Arnold" — the whole name, never a crowd name, for the person who is owed a prize. */
export const fullName = (p: { first_name: string; last_name: string }): string => `${p.first_name} ${p.last_name}`.trim();

/**
 * The prizes to list, in order. The database folds them (pro_drawing_prizes); when a reader meets a backend from
 * before PRO-DRAW-5 (no `prizes`), the rounds or the single prize stand in, with no winner shown — never a crowd
 * name posing as the record.
 */
export function prizesOf(d: Pick<MyDrawing, 'id' | 'prize' | 'prize_image' | 'rounds' | 'prizes'>): Prize[] {
  if (Array.isArray(d.prizes) && d.prizes.length > 0) return d.prizes;
  if (d.rounds?.length) return d.rounds.map((r) => ({ giveaway_id: d.id, no: r.round, prize: r.prize ?? d.prize, prize_image: d.prize_image, winner: null }));
  return [{ giveaway_id: d.id, no: 1, prize: d.prize, prize_image: d.prize_image, winner: null }];
}

/** giveaway_draws.drawn_by in words: 'tv:Lobby' → 'on the TV (Lobby)'; anything else as recorded. */
export function drawnByLine(by: string | null): string {
  if (!by) return '';
  const m = /^tv:(.*)$/.exec(by);
  if (m) return m[1].trim() ? `on the TV (${m[1].trim()})` : 'on the TV';
  return `by ${by}`;
}

/** "Drawn Sat, Oct 17, 4:56 PM on the TV (Lobby) · entry #7 · 21 in the hat" — the record under a winner's name. */
export function drawRecord(w: Winner): string {
  const parts = [`Drawn ${whenLine(w.drawn_at)}${w.drawn_by ? ` ${drawnByLine(w.drawn_by)}` : ''}`.trim()];
  if (w.entry_number != null) parts.push(`entry #${w.entry_number}`);
  if (w.pool_size != null) parts.push(`${w.pool_size} in the hat`);
  return parts.join(' · ');
}

/** "Drawn first: Dev Rao (entry #3), Sat, Oct 17, 4:50 PM — redrawn: not present." (Peter: "redrawn: <reason>") */
export function redrawLine(r: Redraw): string {
  const who = fullName(r) || 'No name given';
  const no = r.entry_number != null ? ` (entry #${r.entry_number})` : '';
  const why = r.reason?.trim() ? `redrawn: ${r.reason.trim()}` : 'redrawn';
  return `Drawn first: ${who}${no}, ${whenLine(r.drawn_at)} — ${why}.`;
}

/**
 * The line under the count. The RULE is the owner's sponsor report's (image-os-web src/lib/sponsor-report.ts
 * reportSummary): counted vs listed. The WORDS differ on purpose: the big number above already says how many
 * signed up, so this line never repeats it, and it gets "was"/"is" right for one person (the report's own
 * sentence says "1 person were told", open item filed 2026-10-14).
 */
export function signupSummary(d: { entries: number; listed: number }): string {
  if (d.entries === 0) return 'No one has signed up yet. When someone scans the QR on your door and enters, they show up here.';
  if (d.listed === d.entries) return d.entries === 1
    ? 'They were told their contact details go to you.'
    : 'Every one of them was told their contact details go to you.';
  const unlisted = d.entries - d.listed;
  const counted = `${people(unlisted)} signed up before that was said, so they are counted but not listed.`;
  if (d.listed === 0) return `None of them were told their contact details go to you, so they are counted but not listed.`;
  return `${people(d.listed)} ${d.listed === 1 ? 'was' : 'were'} told their contact details go to you and ${d.listed === 1 ? 'is' : 'are'} listed below. ${counted}`;
}

/** "Sat, Oct 17, 5:00 PM" in the reader's own time — the studio's pros are in the studio's town. */
export function whenLine(iso: string | null): string {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleString('en-US', { weekday: 'short', month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' });
}

/** Where entries stand right now, in plain words. */
export function entryWindow(d: Pick<MyDrawing, 'status' | 'opens_at' | 'closes_at'>, now: Date = new Date()): string {
  if (d.status === 'archived') return 'This drawing is finished.';
  const opens = d.opens_at ? new Date(d.opens_at) : null;
  const closes = d.closes_at ? new Date(d.closes_at) : null;
  if (opens && opens > now) return `Sign-ups open ${whenLine(d.opens_at)}.`;
  if (closes && closes <= now) return `Sign-ups closed ${whenLine(d.closes_at)}.`;
  return closes ? `Sign-ups are open until ${whenLine(d.closes_at)}.` : 'Sign-ups are open.';
}

/** CSV cells a spreadsheet cannot run — COPIED from image-os-web src/lib/csv-safe.ts (WEB-SEC-038), unchanged. */
const FORMULA_START = /^[=+\-@\t\r]/;
export function neutralizeFormula(v: string): string {
  return FORMULA_START.test(v) ? `'${v}` : v;
}
export function csvCell(v: string): string {
  const safe = neutralizeFormula(v);
  return /[",\n\r]/.test(safe) ? `"${safe.replace(/"/g, '""')}"` : safe;
}

/** "+16175550101" → "(617) 555-0101"; anything else is shown as stored. */
export function phoneLine(p: string | null): string {
  const d = String(p ?? '').replace(/\D/g, '');
  const ten = d.length === 11 && d.startsWith('1') ? d.slice(1) : d;
  return ten.length === 10 ? `(${ten.slice(0, 3)}) ${ten.slice(3, 6)}-${ten.slice(6)}` : String(p ?? '');
}

/** The pro's own list as a spreadsheet: first name, last name, email, phone (blank when the entrant was
 *  not told it goes to the pro), when they signed up. */
export function entrantsCsv(rows: Entrant[]): string {
  const lines = [['First name', 'Last name', 'Email', 'Phone', 'Signed up'], ...rows.map((r) => [r.first_name, r.last_name, r.email, phoneLine(r.phone), whenLine(r.entered_at)])];
  return `${lines.map((l) => l.map(csvCell).join(',')).join('\r\n')}\r\n`;
}
